'use client';

import Link from 'next/link';
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/contexts/auth-context';
import { toast } from 'sonner';
import { validateInviteToken, processInviteSignup } from '@/lib/utils/invite';
import { AuthError, User } from '@supabase/supabase-js';
import { cn } from '@/lib/utils';
import { signInWithGoogle } from '@/lib/actions/auth-actions';

interface SignupResponse {
  error: AuthError | null;
  data?: {
    user: User | null;
  };
}

function SignupForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  const { signUp } = useAuth();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isInviteLoading, setIsInviteLoading] = useState(true);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<{
    name: string;
    logo_url?: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'candidate'
  });

  useEffect(() => {
    const token = searchParams.get('token');
    const role = searchParams.get('role');

    // Set the role from URL parameter if available
    if (role === 'company' || role === 'candidate') {
      setFormData(prev => ({
        ...prev,
        role
      }));
    }

    if (token) {
      setInviteToken(token);
      validateInvite(token);
    } else {
      setIsInviteLoading(false);
    }
  }, [searchParams]);

  const validateInvite = async (token: string) => {
    try {
      const { valid, invite, error } = await validateInviteToken(token);

      if (valid && invite) {
        // Auto-fill email from the invite
        setFormData(prev => ({
          ...prev,
          email: invite.email,
          role: 'company'
        }));

        // Set company info for display
        if (invite.companies) {
          setCompanyInfo({
            name: invite.companies.name,
            logo_url: invite.companies.logo_url
          });
        }
      } else if (error) {
        toast.error(error);
      }
    } catch (error) {
      console.error('Error validating invite:', error);
      toast.error(
        'Could not validate invitation. Please try again or contact support.'
      );
    } finally {
      setIsInviteLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setIsLoading(true);

      // For invited users, hard-code the role to 'company_user'
      const role = inviteToken ? 'company' : formData.role;

      const response = (await signUp(formData.email, formData.password, {
        role: role
      })) as SignupResponse;

      if (response.error) {
        throw response.error;
      }

      // If this is an invite signup and we have a user ID, process the invite
      if (inviteToken && response.data?.user?.id) {
        const { success, error: inviteError } = await processInviteSignup(
          inviteToken,
          response.data.user.id
        );

        if (!success) {
          throw new Error(inviteError || 'Failed to process invite');
        }
      }

      toast.success(
        'Signed up successfully! Please check your email to confirm your account.'
      );
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to sign up';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      // Use inviteToken ? 'company' : formData.role to respect invite flow
      const role = inviteToken ? 'company' : formData.role;
      await signInWithGoogle(role);
    } catch (error) {
      console.error('Error signing up with Google:', error);
      toast.error('Failed to sign up with Google');
    }
  };

  if (isInviteLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="mb-2">Validating invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardHeader className="text-center">
          {inviteToken && companyInfo ? (
            <>
              <div className="flex flex-col items-center mb-4">
                {companyInfo.logo_url ? (
                  <div className="relative w-20 h-20 mb-2">
                    <Image
                      src={companyInfo.logo_url}
                      alt={`${companyInfo.name} logo`}
                      fill
                      className="object-contain"
                    />
                  </div>
                ) : null}
                <CardTitle className="text-xl">
                  Join {companyInfo.name}
                </CardTitle>
                <CardDescription>
                  Create your account to accept the invitation
                </CardDescription>
              </div>
            </>
          ) : (
            <>
              <CardTitle className="text-xl">Create an account</CardTitle>
              <CardDescription>Sign up with Google account</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              {!inviteToken && (
                <>
                  <div className="flex flex-col gap-4">
                    <Button
                      variant="outline"
                      className="w-full"
                      type="button"
                      onClick={handleGoogleSignUp}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="mr-2 h-5 w-5"
                      >
                        <path
                          d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                          fill="currentColor"
                        />
                      </svg>
                      Sign up with Google
                    </Button>
                  </div>
                  <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                    <span className="relative z-10 bg-background px-2 text-muted-foreground">
                      Or continue with email
                    </span>
                  </div>
                </>
              )}
              <div className="grid gap-6">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="m@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isLoading || inviteToken !== null}
                  />
                  {inviteToken && (
                    <p className="text-xs text-muted-foreground">
                      Email is pre-filled from your invitation
                    </p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading
                    ? 'Creating account...'
                    : inviteToken
                      ? 'Accept Invitation'
                      : 'Create Account'}
                </Button>
              </div>
              <div className="text-center text-sm">
                Already have an account?{' '}
                <Link href="/login" className="underline underline-offset-4">
                  Login
                </Link>
              </div>

              {/* {searchParams.get('role') === 'candidate' && (
                <div className="text-center text-sm">
                  Signup as company?{' '}
                  <Link
                    href="/signup?role=company"
                    className="underline underline-offset-4"
                  >
                    Signup
                  </Link>
                </div>
              )} */}

              {searchParams.get('role') === 'company' && (
                <div className="text-center text-sm">
                  Signup as candidate?{' '}
                  <Link
                    href="/signup?role=candidate"
                    className="underline underline-offset-4"
                  >
                    Signup
                  </Link>
                </div>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary">
        By clicking continue, you agree to our{' '}
        <a href="/tos">Terms of Service</a> and{' '}
        <a href="/privacy">Privacy Policy</a>.
      </div>
    </div>
  );
}

export default function SignupPage() {
  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-12">
      <Suspense fallback={<div>Loading...</div>}>
        <SignupForm className="w-full max-w-xl" />
      </Suspense>
    </div>
  );
}
